import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, MenuItem, Select, FormControl, InputLabel,
  Tooltip, Avatar, CircularProgress, Alert, IconButton,
  Grid, Divider, Switch, FormControlLabel, Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon, Add as AddIcon,
  Edit as EditIcon, Delete as DeleteIcon,
  PictureAsPdf as PdfIcon, Close as CloseIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import api from '../services/api';

const AD_TYPES   = ['Short', 'Integrated', 'Reel', 'Brand Film', 'Product Review', 'Shoutout', 'Other'];
const PLATFORMS  = ['YouTube', 'Instagram', 'Facebook', 'All', 'Other'];
const SOURCES    = ['WhatsApp', 'Email', 'Direct Call', 'Referral', 'Other'];
const STATUSES   = ['New', 'Negotiating', 'Accepted', 'In Production', 'Delivered', 'Payment Received', 'Rejected', 'On Hold'];
const PAY_STATUS = ['Pending', 'Partial', 'Received'];

// Statuses that require all financial fields
const FINANCIAL_REQUIRED_STATUSES = new Set(['Accepted', 'In Production', 'Delivered', 'Payment Received']);

const STATUS_COLOR = {
  'New': 'primary', 'Negotiating': 'warning', 'Accepted': 'success',
  'In Production': 'secondary', 'Delivered': 'info',
  'Payment Received': 'success', 'Rejected': 'error', 'On Hold': 'default'
};
const PAY_COLOR = { Pending: 'warning', Partial: 'info', Received: 'success' };

const INR = (v) => v ? `₹${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const EMPTY = {
  enquiryDate: new Date().toISOString().split('T')[0],
  agencyName: '', agencyContact: '', agencyPhone: '', agencyEmail: '',
  brandName: '', brandContact: '', brandPhone: '', brandEmail: '',
  adTitle: '', adType: 'Short', numberOfAds: 1, channels: [], platform: 'YouTube', source: 'WhatsApp',
  quotedAmount: '', agreedAmount: '', paymentStatus: 'Pending', paymentDate: '',
  status: 'New', rejectionReason: '', deliveryDate: '',
  scriptApproved: false, personInChargeId: '', contentLink: '', notes: ''
};

function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
}

// Build validation errors for the current form state
function validate(form) {
  const e = {};
  const isValidNum = (v) => v !== '' && v !== null && v !== undefined && !isNaN(parseFloat(v));

  if (!form.enquiryDate)        e.enquiryDate  = 'Required';
  if (!form.source)             e.source       = 'Required';
  if (!form.status)             e.status       = 'Required';
  if (!form.agencyName?.trim()) e.agencyName   = 'Agency Name is required';
  if (!form.brandName?.trim())  e.brandName    = 'Brand Name is required';
  if (!form.adTitle?.trim())    e.adTitle      = 'Ad Title is required';
  if (!form.adType)             e.adType       = 'Required';
  if (!form.platform)           e.platform     = 'Required';
  if (!Array.isArray(form.channels) || !form.channels.length)
    e.channels = 'Select at least one channel';

  if (FINANCIAL_REQUIRED_STATUSES.has(form.status)) {
    if (!isValidNum(form.quotedAmount))  e.quotedAmount  = 'Enter a valid amount';
    if (!isValidNum(form.agreedAmount))  e.agreedAmount  = 'Enter a valid amount';
    if (!form.paymentStatus)             e.paymentStatus = 'Required for this status';
    if (!form.deliveryDate)              e.deliveryDate  = 'Required for this status';
  }
  // Payment date mandatory when payment received or partial
  const validDate = (d) => d && d !== 'Invalid date' && /^\d{4}-\d{2}-\d{2}$/.test(d);
  if ((form.paymentStatus === 'Received' || form.paymentStatus === 'Partial') && !validDate(form.paymentDate)) {
    e.paymentDate = 'Payment Date is required when payment is received';
  }
  return e;
}

export default function AdManagement() {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType]     = useState('');
  const [users, setUsers]         = useState([]);
  const [contacts, setContacts]   = useState([]); // for auto-populate
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [brandOptions, setBrandOptions]   = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [formErrors, setFormErrors] = useState({});
  const [submitted, setSubmitted] = useState(false); // only show errors after first submit attempt
  const [saving, setSaving]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (search)       params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterType)   params.adType = filterType;
      const res = await api.get('/ads', { params });
      setRows(res.data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load');
    } finally { setLoading(false); }
  }, [search, filterStatus, filterType]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    api.get('/users', { params: { isActive: 'true' } })
       .then(r => setUsers(r.data.data?.users || r.data.data || []))
       .catch(() => {});
    api.get('/dropdowns')
       .then(r => { setChannelOptions(r.data.data?.channels || []); })
       .catch(() => {});
    // Load contacts for auto-populate + agency/brand options from Contact Diary
    api.get('/contacts', { params: { status: 'Active' } })
       .then(r => {
         const all = r.data.data || [];
         setContacts(all);
         setAgencyOptions(
           all.filter(c => c.contactType === 'Ad Agency')
              .map(c => c.company || c.fullName).filter(Boolean)
         );
         setBrandOptions(
           all.filter(c => c.contactType === 'Brand')
              .map(c => c.company || c.fullName).filter(Boolean)
         );
       })
       .catch(() => {});
  }, []);

  // Live validation — only display errors once user has tried to submit
  useEffect(() => {
    if (submitted) setFormErrors(validate(form));
  }, [form, submitted]);

  const openAdd  = () => {
    setEditTarget(null);
    setForm(EMPTY);
    setFormErrors({});
    setSubmitted(false);
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditTarget(r);
    setForm({
      enquiryDate:      r.enquiryDate      || EMPTY.enquiryDate,
      agencyName:       r.agencyName       || '',
      agencyContact:    r.agencyContact    || '',
      agencyPhone:      r.agencyPhone      || '',
      agencyEmail:      r.agencyEmail      || '',
      brandName:        r.brandName        || '',
      brandContact:     r.brandContact     || '',
      brandPhone:       r.brandPhone       || '',
      brandEmail:       r.brandEmail       || '',
      adTitle:          r.adTitle          || '',
      adType:           r.adType           || 'Short',
      numberOfAds:      r.numberOfAds      || 1,
      channels: (() => {
        const c = r.channels;
        if (Array.isArray(c)) return c;
        if (typeof c === 'string' && c) { try { return JSON.parse(c); } catch { return []; } }
        return [];
      })(),
      platform:         r.platform         || 'YouTube',
      source:           r.source           || 'WhatsApp',
      quotedAmount:     r.quotedAmount     || '',
      agreedAmount:     r.agreedAmount     || '',
      paymentStatus:    r.paymentStatus    || 'Pending',
      paymentDate: (r.paymentDate && r.paymentDate !== 'Invalid date') ? r.paymentDate : '',
      status:           r.status           || 'New',
      rejectionReason:  r.rejectionReason  || '',
      deliveryDate:     r.deliveryDate     || '',
      scriptApproved:   r.scriptApproved   || false,
      personInChargeId: r.personInChargeId || '',
      contentLink:      r.contentLink      || '',
      notes:            r.notes            || '',
    });
    setFormErrors({});
    setSubmitted(false);
    setDialogOpen(true);
  };

  // Auto-populate contact fields when agency/brand name is selected and matches a contact.
  // Always reset contact fields first, then repopulate if a match is found.
  const handleAgencyChange = (val) => {
    const name = val || '';
    const match = contacts.find(c =>
      c.fullName?.toLowerCase() === name.toLowerCase() ||
      c.company?.toLowerCase() === name.toLowerCase()
    );
    setForm(p => ({
      ...p,
      agencyName:    name,
      agencyContact: match ? (match.fullName || '') : '',
      agencyPhone:   match ? (match.mobile || match.phone || '') : '',
      agencyEmail:   match ? (match.email || '') : '',
    }));
  };

  const handleBrandChange = (val) => {
    const name = val || '';
    const match = contacts.find(c =>
      c.fullName?.toLowerCase() === name.toLowerCase() ||
      c.company?.toLowerCase() === name.toLowerCase()
    );
    setForm(p => ({
      ...p,
      brandName:    name,
      brandContact: match ? (match.fullName || '') : '',
      brandPhone:   match ? (match.mobile || match.phone || '') : '',
      brandEmail:   match ? (match.email || '') : '',
    }));
  };

  const handleSave = async () => {
    setSubmitted(true);
    const errors = validate(form);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      if (editTarget) await api.put(`/ads/${editTarget.id}`, form);
      else            await api.post('/ads', form);
      setDialogOpen(false);
      fetchRows();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/ads/${deleteTarget.id}`);
      setDeleteTarget(null); fetchRows();
    } catch (e) { setError('Failed to delete'); }
  };

  const handleInvoice = async (row) => {
    setPdfLoading(row.id);
    try {
      const res = await api.get(`/ads/${row.id}/invoice`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `AdID_${row.invoiceNumber}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { setError('Failed to generate invoice'); }
    finally { setPdfLoading(null); }
  };

  const f = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));
  const fBool = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.checked }));

  // When main status → Payment Received, auto-set paymentStatus to Received
  const handleStatusChange = (e) => {
    const val = e.target.value;
    setForm(p => ({
      ...p,
      status: val,
      ...(val === 'Payment Received' ? { paymentStatus: 'Received' } : {})
    }));
  };

  // When paymentStatus → Received, auto-set main status to Payment Received
  const handlePaymentStatusChange = (e) => {
    const val = e.target.value;
    setForm(p => ({
      ...p,
      paymentStatus: val,
      ...(val === 'Received' ? { status: 'Payment Received' } : {})
    }));
  };

  const showReject = form.status === 'Rejected';
  const finRequired = FINANCIAL_REQUIRED_STATUSES.has(form.status);
  const err = formErrors; // shorthand

  // Agency/brand options come directly from Contact Diary (type = 'Ad Agency' / 'Brand')
  // agencyOptions and brandOptions are already filtered in the useEffect above
  const agencyAllOptions = useMemo(() => [...new Set(agencyOptions)].sort(), [agencyOptions]);
  const brandAllOptions  = useMemo(() => [...new Set(brandOptions)].sort(),  [brandOptions]);

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          📢 Ad / Collaboration
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>New Enquiry</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Filters */}
      <Box display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
        <TextField size="small" placeholder="Search brand, agency…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: '1 1 160px', minWidth: 0 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <FormControl size="small" sx={{ flex: '1 1 120px', minWidth: 0 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: '1 1 110px', minWidth: 0 }}>
          <InputLabel>Ad Type</InputLabel>
          <Select value={filterType} label="Ad Type" onChange={e => setFilterType(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {AD_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{rows.length} enquier{rows.length !== 1 ? 'ies' : 'y'}</Typography>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Ad ID</strong></TableCell>
              <TableCell><strong>Brand / Agency</strong></TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Type</strong></TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Platform</strong></TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Agreed Amt</strong></TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Payment</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>In Charge</strong></TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Delivery</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No enquiries found</TableCell></TableRow>
            ) : rows.map(row => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.invoiceNumber}</Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: { xs: 140, sm: 'none' } }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main', flexShrink: 0 }}>
                      {initials(row.brandName)}
                    </Avatar>
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{row.brandName}</Typography>
                      {row.agencyName && <Typography variant="caption" color="text.secondary" noWrap>{row.agencyName}</Typography>}
                      <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                        <Chip label={row.adType} size="small" sx={{ height: 18, fontSize: '0.62rem' }} />
                        {(row.agreedAmount || row.quotedAmount) && (
                          <Typography variant="caption" fontWeight={600}>{INR(row.agreedAmount || row.quotedAmount)}</Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Chip label={row.adType} size="small" /></TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="body2">{row.platform || '—'}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2" fontWeight={600}>{INR(row.agreedAmount || row.quotedAmount)}</Typography>
                  {row.quotedAmount && row.agreedAmount && parseFloat(row.quotedAmount) !== parseFloat(row.agreedAmount) && (
                    <Typography variant="caption" color="text.secondary">Quoted: {INR(row.quotedAmount)}</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Chip label={row.paymentStatus} size="small" color={PAY_COLOR[row.paymentStatus] || 'default'} />
                </TableCell>
                <TableCell><Chip label={row.status} size="small" color={STATUS_COLOR[row.status] || 'default'} /></TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="body2">{row.personInCharge?.fullName || '—'}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2">{fmt(row.deliveryDate)}</Typography>
                </TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Generate Invoice PDF">
                    <span>
                      <IconButton size="small" color="primary" onClick={() => handleInvoice(row)} disabled={pdfLoading === row.id}>
                        {pdfLoading === row.id ? <CircularProgress size={14} /> : <PdfIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" color="info" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { m: { xs: 1, sm: 2 }, width: '100%', maxHeight: { xs: '95vh', sm: '90vh' } } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon color="primary" />
            {editTarget ? `Edit — ${editTarget.invoiceNumber}` : 'New Ad Enquiry'}
          </Box>
          <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          {submitted && Object.keys(err).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Please fill in all required fields before saving.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ pt: 0.5 }}>

            {/* ── Enquiry Info ── */}
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Enquiry Date *" type="date"
                InputLabelProps={{ shrink: true }} value={form.enquiryDate} onChange={f('enquiryDate')}
                error={!!err.enquiryDate} helperText={err.enquiryDate} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" error={!!err.source}>
                <InputLabel>Source *</InputLabel>
                <Select value={form.source} label="Source *" onChange={f('source')}>
                  {SOURCES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
                {err.source && <Typography variant="caption" color="error" sx={{ mt: 0.3, ml: 1.5 }}>{err.source}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" error={!!err.status}>
                <InputLabel>Status *</InputLabel>
                <Select value={form.status} label="Status *" onChange={handleStatusChange}>
                  {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
                {err.status && <Typography variant="caption" color="error" sx={{ mt: 0.3, ml: 1.5 }}>{err.status}</Typography>}
              </FormControl>
            </Grid>
            {showReject && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Reason for Rejection"
                  value={form.rejectionReason} onChange={f('rejectionReason')} />
              </Grid>
            )}

            {/* ── Agency ── */}
            <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">AGENCY</Typography></Divider></Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={agencyAllOptions}
                value={form.agencyName}
                onInputChange={(_, val) => handleAgencyChange(val)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" label="Agency Name *"
                    error={!!err.agencyName} helperText={err.agencyName} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Agency Contact Person"
                value={form.agencyContact} onChange={f('agencyContact')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Agency Phone"
                value={form.agencyPhone} onChange={f('agencyPhone')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Agency Email"
                value={form.agencyEmail} onChange={f('agencyEmail')} />
            </Grid>

            {/* ── Brand ── */}
            <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">BRAND</Typography></Divider></Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={brandAllOptions}
                value={form.brandName}
                onInputChange={(_, val) => handleBrandChange(val)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" label="Brand Name *"
                    error={!!err.brandName} helperText={err.brandName} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Brand Contact Person"
                value={form.brandContact} onChange={f('brandContact')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Brand Phone"
                value={form.brandPhone} onChange={f('brandPhone')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Brand Email"
                value={form.brandEmail} onChange={f('brandEmail')} />
            </Grid>

            {/* ── Ad Details ── */}
            <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">AD DETAILS</Typography></Divider></Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth size="small" label="Ad Title *" value={form.adTitle} onChange={f('adTitle')}
                placeholder="e.g. Car Street Karur — Brand Integration"
                error={!!err.adTitle} helperText={err.adTitle} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Number of Ads" type="number"
                inputProps={{ min: 1 }} value={form.numberOfAds} onChange={f('numberOfAds')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" error={!!err.adType}>
                <InputLabel>Type of Ad *</InputLabel>
                <Select value={form.adType} label="Type of Ad *" onChange={f('adType')}>
                  {AD_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
                {err.adType && <Typography variant="caption" color="error" sx={{ mt: 0.3, ml: 1.5 }}>{err.adType}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" error={!!err.platform}>
                <InputLabel>Platform *</InputLabel>
                <Select value={form.platform} label="Platform *" onChange={f('platform')}>
                  {PLATFORMS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
                {err.platform && <Typography variant="caption" color="error" sx={{ mt: 0.3, ml: 1.5 }}>{err.platform}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                multiple freeSolo
                options={channelOptions}
                value={form.channels}
                onChange={(_, val) => setForm(p => ({ ...p, channels: val }))}
                renderTags={(val, getTagProps) =>
                  val.map((opt, idx) => <Chip label={opt} size="small" {...getTagProps({ index: idx })} />)
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" label="Channels *"
                    placeholder="Select or type"
                    error={!!err.channels} helperText={err.channels} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={`Delivery Date${finRequired ? ' *' : ''}`} type="date"
                InputLabelProps={{ shrink: true }} value={form.deliveryDate} onChange={f('deliveryDate')}
                error={!!err.deliveryDate} helperText={err.deliveryDate} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Content / Script Link"
                value={form.contentLink} onChange={f('contentLink')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={<Switch checked={!!form.scriptApproved} onChange={fBool('scriptApproved')} />}
                label="Script / Content Approved" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Person In Charge</InputLabel>
                <Select value={form.personInChargeId} label="Person In Charge" onChange={f('personInChargeId')}>
                  <MenuItem value="">— None —</MenuItem>
                  {users.map(u => <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* ── Financials ── */}
            <Grid item xs={12}>
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  FINANCIALS {finRequired && <span style={{ color: '#d32f2f' }}>— required for {form.status}</span>}
                </Typography>
              </Divider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label={`Quoted Amount (₹)${finRequired ? ' *' : ''}`} type="number"
                value={form.quotedAmount} onChange={f('quotedAmount')}
                error={!!err.quotedAmount} helperText={err.quotedAmount} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label={`Agreed Amount (₹)${finRequired ? ' *' : ''}`} type="number"
                value={form.agreedAmount} onChange={f('agreedAmount')}
                error={!!err.agreedAmount} helperText={err.agreedAmount} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" error={!!err.paymentStatus}>
                <InputLabel>Payment Status{finRequired ? ' *' : ''}</InputLabel>
                <Select value={form.paymentStatus} label={`Payment Status${finRequired ? ' *' : ''}`} onChange={handlePaymentStatusChange}>
                  {PAY_STATUS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
                {err.paymentStatus && <Typography variant="caption" color="error" sx={{ mt: 0.3, ml: 1.5 }}>{err.paymentStatus}</Typography>}
              </FormControl>
            </Grid>
            {form.paymentStatus !== 'Pending' && (
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small"
                  label={form.paymentStatus === 'Pending' ? 'Payment Date' : 'Payment Date *'}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.paymentDate}
                  onChange={f('paymentDate')}
                  error={!!err.paymentDate}
                  helperText={err.paymentDate} />
              </Grid>
            )}

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" value={form.notes}
                onChange={f('notes')} multiline rows={3} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : (editTarget ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { m: { xs: 1, sm: 2 } } }}>
        <DialogTitle>Delete Enquiry</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteTarget?.invoiceNumber}</strong> — {deleteTarget?.brandName}? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
